import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion, AnimatePresence } from 'framer-motion';
import { User, MapPin, Sprout, CheckCircle, ArrowRight, ArrowLeft, Edit, Award, Upload, X, FileText, Phone, Smartphone, ChevronLeft, ChevronRight } from 'lucide-react';
import ApiService from '../services/api';
import theme from '../theme';
import bhoomeeQR from '../assets/bhoomee.png';
import googlePlayImg from '../assets/googlePlay.png';
import appStoreImg from '../assets/appStore.png';
import { logCustomEvent } from '../firebase';

// Custom StepConnector for elegant golden gradient lines
const CustomStepConnector = () => (
  <StepConnector
    sx={{
      '& .MuiStepConnector-line': {
        height: 2,
        border: 0,
        backgroundColor: 'rgba(212, 167, 71, 0.15)',
        borderRadius: 1,
        marginTop: '-1px',
        position: 'relative',
        zIndex: 1,
      },
      '&.Mui-active .MuiStepConnector-line': {
        background: 'linear-gradient(90deg, #D4A747 0%, #B8901C 100%)',
      },
      '&.Mui-completed .MuiStepConnector-line': {
        background: 'linear-gradient(90deg, #D4A747 0%, #B8901C 100%)',
      },
    }}
  />
);

const AwardsApplication = () => {
  const { t, i18n } = useTranslation('awards');
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Phone verification state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [userId, setUserId] = useState(null);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [hasReadInstructions, setHasReadInstructions] = useState(false);

  // Stepper scroll state
  const stepperRef = React.useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    // Region and Category Selection
    role: '',
    regionLevel: '', // 'District' or 'Zonal'
    category: '',

    // Personal Details
    fullName: '',
    aadharNumber: '',
    email: '',
    phoneNumber: '',
    state: 'Karnataka',
    district: '',
    zone: '',
    taluk: '',
    hobli: '',
    village: '',

    // Farming Profile
    yearsOfExperience: '',
    domain: '',
    landUnderCultivation: '',
    cropDetails: '',

    // Achievements & Proof - Per Criterion
    criteriaResponses: [], // Array of {criterionName: string, achievementDescription: string, documents: [], noDocumentsReason: string}
  });

  const [formErrors, setFormErrors] = useState({});

  // Dynamic steps based on user role - exclude Farming Profile for FPO and innovators
  const getSteps = () => {
    const baseSteps = [
      t('stepper.categorySelection'),
      t('stepper.phoneVerification'),
      t('stepper.personalDetails')
    ];

    // Only include Farming Profile for individual farmers
    if (formData.role === 'farmer') {
      baseSteps.push(t('stepper.farmingProfile'));
    }

    baseSteps.push(t('stepper.achievementsProof'), t('stepper.reviewSubmit'));
    return baseSteps;
  };

  const steps = getSteps();

  // Helper function to get step name by index
  const getStepName = (stepIndex) => {
    return steps[stepIndex];
  };

  // Modern field styling matching Sponsor form
  const modernFieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: 'rgba(212, 167, 71, 0.02)',
      border: '1px solid rgba(212, 167, 71, 0.15)',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: 'rgba(212, 167, 71, 0.04)',
        borderColor: 'rgba(212, 167, 71, 0.3)',
        '& fieldset': { border: 'none' }
      },
      '&.Mui-focused': {
        backgroundColor: 'rgba(212, 167, 71, 0.05)',
        borderColor: '#D4A747',
        boxShadow: '0 0 0 3px rgba(212, 167, 71, 0.1)',
        '& fieldset': { border: 'none' }
      },
      '&.Mui-error': {
        backgroundColor: 'rgba(244, 67, 54, 0.02)',
        borderColor: 'rgba(244, 67, 54, 0.3)',
        '&:hover': {
          backgroundColor: 'rgba(244, 67, 54, 0.04)',
          borderColor: 'rgba(244, 67, 54, 0.5)'
        },
        '&.Mui-focused': {
          backgroundColor: 'rgba(244, 67, 54, 0.05)',
          borderColor: '#f44336',
          boxShadow: '0 0 0 3px rgba(244, 67, 54, 0.1)'
        }
      },
      '& fieldset': { border: 'none' }
    },
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
      fontSize: '0.875rem',
      fontWeight: 500,
      backgroundColor: 'transparent',
      '&.MuiInputLabel-shrink': {
        backgroundColor: 'white',
        padding: '0 6px',
        marginLeft: '-6px',
        borderRadius: '4px'
      },
      '&.Mui-focused': {
        color: '#D4A747'
      },
      '&.Mui-error': {
        color: '#f44336'
      }
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.75rem',
      marginLeft: 0,
      marginTop: '6px',
      fontWeight: 400
    }
  };

  const zoneMapping = {
    'Zone 1': ['Mysuru', 'Mandya', 'Ramanagara', 'Chamarajanagar', 'Hassan'],
    'Zone 2': ['Bengaluru Rural', 'Chikkaballapur', 'Kolar', 'Chitradurga', 'Tumakuru'],
    'Zone 3': ['Davanagere', 'Shivamogga', 'Chikkamagaluru', 'Ballari', 'Dakshina Kannada', 'Udupi'],
    'Zone 4': ['Dharwad', 'Belagavi', 'Gadag', 'Koppal', 'Haveri', 'Uttara Kannada'],
    'Zone 5': ['Vijayapura', 'Bagalkot', 'Kalaburagi', 'Bidar', 'Raichur', 'Yadgir']
  };

  // Get roles from translations
  const roles = [
    { value: 'farmer', label: t('categorySelection.role.farmer') },
    { value: 'fpo', label: t('categorySelection.role.fpo') },
    { value: 'innovation', label: t('categorySelection.role.innovator') }
  ];

  const farmingDomains = [
    { value: 'Agriculture', label: t('farmingProfile.domains.agriculture') },
    { value: 'Horticulture', label: t('farmingProfile.domains.horticulture') },
    { value: 'Floriculture', label: t('farmingProfile.domains.floriculture') },
    { value: 'Mixed Farming', label: t('farmingProfile.domains.mixedFarming') }
  ];

  // Helper function to get translated domain label
  const getDomainLabel = (domainValue) => {
    const domain = farmingDomains.find(d => d.value === domainValue);
    return domain ? domain.label : domainValue;
  };

  // Auto-detect zone based on district
  useEffect(() => {
    if (formData.district) {
      const zone = Object.keys(zoneMapping).find(z =>
        zoneMapping[z].includes(formData.district)
      );
      if (zone) {
        setFormData(prev => ({ ...prev, zone }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.district]);

  // Fetch categories from API
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const currentLang = i18n.language || 'en';
      console.log(`Fetching award categories for language: ${currentLang}`);
      const data = await ApiService.getCategories(currentLang);
      console.log(`Fetched ${data.length} categories for language: ${currentLang}`);
      setCategories(data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setError('Failed to load award categories. Please try again.');
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch categories when component mounts, when user reaches category selection step, or when language changes
  useEffect(() => {
    if (activeStep === 0) {
      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, i18n.language]);

  // Filter categories based on role and region level
  useEffect(() => {
    if (formData.role && formData.regionLevel) {
      console.log('Filtering categories by role:', formData.role, 'and regionLevel:', formData.regionLevel);

      // Map form values to language-specific category values
      const categoryMapping = {
        'en': {
          'farmer': 'farmer',
          'fpo': 'fpo',
          'innovation': 'innovation'
        },
        'kn': {
          'farmer': 'ರೈತ',
          'fpo': 'ಎಫ್ ಪಿ ಓ',
          'innovation': 'ನಾವೀನ್ಯತೆ'
        }
      };

      // Map form values to language-specific awardType values
      const awardTypeMapping = {
        'en': {
          'district': 'district',
          'zonal': 'zonal',
          'state': 'state'
        },
        'kn': {
          'district': 'ಜಿಲ್ಲೆ',
          'zonal': 'ವಲಯ (ಝೋನಲ್)',
          'state': 'ರಾಜ್ಯ'
        }
      };

      const currentLang = i18n.language || 'en';
      const categoryValue = categoryMapping[currentLang]?.[formData.role] || formData.role;
      const awardTypeValue = awardTypeMapping[currentLang]?.[formData.regionLevel] || formData.regionLevel;

      console.log(`Language: ${currentLang}, Filtering by category: "${categoryValue}", awardType: "${awardTypeValue}"`);

      const filtered = categories.filter(cat => {
        const matchesRole = cat.category?.toLowerCase() === categoryValue.toLowerCase();
        const matchesRegionLevel = cat.awardType?.toLowerCase() === awardTypeValue.toLowerCase();
        return matchesRole && matchesRegionLevel && cat.isActive;
      });

      console.log('Filtered categories:', filtered.length, 'categories found');
      setFilteredCategories(filtered);
    } else {
      console.log('Clearing filtered categories - role or regionLevel not selected');
      setFilteredCategories([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.role, formData.regionLevel, categories, i18n.language]);

  // Pre-fill form from URL query parameters (from Nominations page)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const role = params.get('role');
    const category = params.get('category');
    const awardType = params.get('awardType');

    if (role || category || awardType) {
      console.log('Pre-filling form from URL params:', { role, category, awardType });

      setFormData(prev => ({
        ...prev,
        ...(role && { role }),
        ...(category && { category }),
        ...(awardType && { regionLevel: awardType })
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Initialize criteriaResponses when category changes
  useEffect(() => {
    if (formData.category && categories.length > 0) {
      const selectedCategory = categories.find(c => c._id === formData.category);
      if (selectedCategory && selectedCategory.qualificationCriteria && selectedCategory.qualificationCriteria.length > 0) {
        // Only initialize if criteriaResponses is empty or undefined
        if (!formData.criteriaResponses || formData.criteriaResponses.length === 0) {
          const newResponses = selectedCategory.qualificationCriteria.map(criterion => ({
            criterionName: criterion,
            achievementDescription: '',
            documents: [],
            noDocumentsReason: ''
          }));

          setFormData(prev => ({
            ...prev,
            criteriaResponses: newResponses
          }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.category, categories]);

  // Send OTP
  const handleSendOtp = async () => {
    // Validate phone number
    if (!phoneNumber || !/^[6-9]\d{9}$/.test(phoneNumber)) {
      setFormErrors({ phoneNumber: 'Please enter a valid 10-digit Indian mobile number' });
      return;
    }

    setSendingOtp(true);
    setError('');

    try {
      // For farmers and FPOs, verify if phone number is registered in Bhoomee app first
      if (formData.role !== 'innovation') {
        const userIdResponse = await fetch(`https://bhoomee.tedoratech.com/user/getuserId/${phoneNumber}`);

        if (!userIdResponse.ok) {
          // User not registered - show download app message
          setOtpVerified(true);
          setUserId(null);
          setSendingOtp(false);
          return;
        }

        const userIdData = await userIdResponse.json();

        // Check if the response indicates success and user exists
        if (userIdData.status === 'SUCCESS' && !userIdData.userId) {
          // User not registered - show download app message
          setOtpVerified(true);
          setUserId(null);
          setSendingOtp(false);
          return;
        }
      }

      // Check for duplicate submission before sending OTP
      const duplicateCheck = await ApiService.checkDuplicateSubmission(phoneNumber, formData.category);

      if (duplicateCheck.exists) {
        throw new Error('You have already submitted an application for this category with this phone number. Duplicate submissions are not allowed.');
      }

      // Proceed to send OTP
      const response = await fetch('https://bhoomee.tedoratech.com/otp/sendOTP', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send OTP');
      }

      setOtpSent(true);
      setFormErrors({});

      // Track OTP sent event
      logCustomEvent('otp_sent', {
        role: formData.role
      });
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');

      // Track OTP send failure
      logCustomEvent('otp_send_failed', {
        role: formData.role,
        error: err.message
      });
    } finally {
      setSendingOtp(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setFormErrors({ otp: 'Please enter a valid 6-digit OTP' });
      return;
    }

    setVerifyingOtp(true);
    setError('');

    try {
      // Check if role is innovator
      if (formData.role === 'innovation') {
        // For innovators, just verify OTP and proceed without fetching user details
        const response = await fetch('https://bhoomee.tedoratech.com/otp/verifyOTP', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ phoneNumber, otp }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Invalid OTP');
        }

        const result = await response.json();

        if (result.status === 'SUCCESS') {
          setOtpVerified(true);
          setUserId('innovator');
          setFormErrors({});

          // Auto-populate phone number for innovators
          setFormData(prev => ({
            ...prev,
            phoneNumber: phoneNumber
          }));

          // Track OTP verification success
          logCustomEvent('otp_verified', {
            role: formData.role
          });

          // Auto-navigate to personal details - use setTimeout to ensure state updates are processed
          setTimeout(() => {
            setActiveStep(2);
          }, 0);
        } else {
          throw new Error('OTP verification failed');
        }
      } else {
        // For farmers and FPOs, verify OTP and fetch user details
        const response = await fetch('https://bhoomee.tedoratech.com/otp/verifyOTP', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ phoneNumber, otp }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Invalid OTP');
        }

        const result = await response.json();

        if (result.status === 'SUCCESS' && result.userId) {
          // User exists, fetch their details based on role
          setUserId(result.userId);
          setOtpVerified(true);
          const userRole = result.userRole; // Get userRole from response
          await fetchUserDetails(result.userId, userRole);
          setFormErrors({});

          // Track OTP verification success
          logCustomEvent('otp_verified', {
            role: formData.role,
            userRole: userRole
          });

          // Auto-navigate to personal details after data is loaded - use setTimeout to ensure state updates are processed
          setTimeout(() => {
            setActiveStep(2);
          }, 0);
        } else if (result.status === 'SUCCESS' && !result.userId) {
          // OTP verified but user doesn't exist, show app download option
          setOtpVerified(true);
          setUserId(null);
          setFormErrors({});

          // Track OTP verified but user not found
          logCustomEvent('otp_verified_user_not_found', {
            role: formData.role
          });
        } else {
          throw new Error('OTP verification failed');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to verify OTP. Please try again.');

      // Track OTP verification failure
      logCustomEvent('otp_verification_failed', {
        role: formData.role,
        error: err.message
      });
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Fetch user details based on role
  const fetchUserDetails = async (userId, userRole) => {
    try {
      let apiUrl;

      // Determine API endpoint based on user role
      if (userRole === 'farmer') {
        apiUrl = `https://bhoomee.tedoratech.com/farmer/getbyid/${userId}`;
      } else if (userRole === 'fpo') {
        apiUrl = `https://bhoomee.tedoratech.com/fpo/getdet/${userId}`;
      } else {
        throw new Error('Invalid user role');
      }

      console.log('Fetching user details from:', apiUrl);
      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error('Failed to fetch user details');
      }

      const result = await response.json();
      console.log('Fetched API response:', result);

      // Extract data from response.data
      const userData = result.data || result;
      console.log('Extracted user data:', userData);

      // Pre-fill form data with user details
      // Field mappings based on actual API response structure
      setFormData(prev => ({
        ...prev,
        // Role is now selected manually by user in Category Selection step
        fullName: userData.name || userData.fullName || userData.farmerName || '',
        aadharNumber: userData.aadhar || userData.aadharNumber || userData.aadhaar || '',
        email: userData.email || '',
        phoneNumber: userData.mobile || userData.phoneNumber || phoneNumber,
        state: userData.state || prev.state,
        district: userData.district || '',
        zone: userData.zone || prev.zone,
        taluk: userData.taluk || userData.taluka || '',
        hobli: userData.hobli || '',
        village: userData.village || '',
        yearsOfExperience: userData.yearsOfExperience || userData.experience || '',
        domain: userData.domain || userData.farmingDomain || userData.farmingType || userData.businessType || '',
        landUnderCultivation: userData.landUnderCultivation || userData.landArea || userData.totalLandArea || '',
        cropDetails: userData.cropDetails || userData.crops || userData.cropType || '',
      }));

      console.log('Form data updated successfully');
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError('Failed to load user details. You can still continue with the application.');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Stepper scroll functionality
  const checkScrollArrows = () => {
    if (stepperRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = stepperRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  const scrollStepper = (direction) => {
    if (stepperRef.current) {
      const scrollAmount = 200;
      stepperRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    checkScrollArrows();
    const handleResize = () => checkScrollArrows();
    window.addEventListener('resize', handleResize);

    const stepperElement = stepperRef.current;
    if (stepperElement) {
      stepperElement.addEventListener('scroll', checkScrollArrows);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (stepperElement) {
        stepperElement.removeEventListener('scroll', checkScrollArrows);
      }
    };
  }, [activeStep]);

  // Handlers for criteria responses
  const handleCriterionAchievementChange = (criterionIndex, value) => {
    setFormData(prev => ({
      ...prev,
      criteriaResponses: prev.criteriaResponses.map((response, index) =>
        index === criterionIndex ? { ...response, achievementDescription: value } : response
      )
    }));

    // Clear error
    const errorKey = `criterionAchievement_${criterionIndex}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleCriterionFileUpload = (criterionIndex, event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const newDocuments = files.map(file => ({
      file,
      reason: '',
      id: Date.now() + Math.random()
    }));

    setFormData(prev => ({
      ...prev,
      criteriaResponses: prev.criteriaResponses.map((response, index) =>
        index === criterionIndex
          ? { ...response, documents: [...(response.documents || []), ...newDocuments] }
          : response
      )
    }));
  };

  const handleCriterionRemoveDocument = (criterionIndex, docId) => {
    setFormData(prev => ({
      ...prev,
      criteriaResponses: prev.criteriaResponses.map((response, index) =>
        index === criterionIndex
          ? { ...response, documents: (response.documents || []).filter(doc => doc.id !== docId) }
          : response
      )
    }));
  };

  const handleCriterionNoDocReasonChange = (criterionIndex, value) => {
    setFormData(prev => ({
      ...prev,
      criteriaResponses: prev.criteriaResponses.map((response, index) =>
        index === criterionIndex ? { ...response, noDocumentsReason: value } : response
      )
    }));

    // Clear error
    const errorKey = `criterionNoDocReason_${criterionIndex}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const validateStep = (step) => {
    const errors = {};
    const stepName = getStepName(step);

    switch (stepName) {
      case t('stepper.categorySelection'):
        if (!formData.role) errors.role = t('categorySelection.errors.role');
        if (!formData.category) errors.category = t('categorySelection.errors.category');
        if (!formData.regionLevel) errors.regionLevel = t('categorySelection.errors.regionLevel');
        break;

      case t('stepper.phoneVerification'):
        if (!hasReadInstructions) {
          errors.hasReadInstructions = t('categorySelection.errors.hasReadInstructions');
        }
        if (!otpVerified) {
          errors.phoneVerification = t('phoneVerification.errors.phoneVerification');
        }
        if (otpVerified && formData.role !== 'innovation' && !userId) {
          errors.phoneVerification = t('phoneVerification.errors.appRegistration');
        }
        break;

      case t('stepper.personalDetails'):
        if (!formData.fullName) errors.fullName = t('personalDetails.errors.fullName');
        // Aadhar is optional, but validate format if provided
        if (formData.aadharNumber && !/^\d{12}$/.test(formData.aadharNumber.replace(/\s/g, ''))) {
          errors.aadharNumber = t('personalDetails.errors.aadharNumber');
        }
        if (!formData.phoneNumber) {
          errors.phoneNumber = t('personalDetails.errors.phoneNumber');
        } else if (!/^\d{10}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
          errors.phoneNumber = t('personalDetails.errors.phoneNumber');
        }
        if (!formData.district) errors.district = t('personalDetails.errors.district');
        if (!formData.taluk) errors.taluk = t('personalDetails.errors.taluk');
        if (!formData.hobli) errors.hobli = t('personalDetails.errors.hobli');
        if (!formData.village) errors.village = t('personalDetails.errors.village');
        break;

      case t('stepper.farmingProfile'):
        //Only validate for farmers (not for FPO or innovators)
        if (formData.role === 'farmer') {
          if (!formData.yearsOfExperience) {
            errors.yearsOfExperience = t('farmingProfile.errors.yearsOfExperience');
          } else if (formData.yearsOfExperience < 0) {
            errors.yearsOfExperience = 'Years of experience cannot be negative';
          }
          if (!formData.domain) errors.domain = t('farmingProfile.errors.domain');
          if (!formData.landUnderCultivation) {
            errors.landUnderCultivation = t('farmingProfile.errors.landUnderCultivation');
          } else if (formData.landUnderCultivation < 0) {
            errors.landUnderCultivation = 'Land area cannot be negative';
          }
          if (!formData.cropDetails) errors.cropDetails = t('farmingProfile.errors.cropDetails');
        }
        break;

      case t('stepper.achievementsProof'):
        // Validate each criterion response
        if (formData.criteriaResponses && formData.criteriaResponses.length > 0) {
          formData.criteriaResponses.forEach((response, criterionIndex) => {
            // Validate achievement description
            if (!response.achievementDescription || response.achievementDescription.trim() === '') {
              errors[`criterionAchievement_${criterionIndex}`] = t('achievementsProof.achievementDescription.error');
            } else if (response.achievementDescription.trim().length < 50) {
              errors[`criterionAchievement_${criterionIndex}`] = t('achievementsProof.achievementDescription.minLength');
            }

            // If no documents uploaded, reason is required
            if (!response.documents || response.documents.length === 0) {
              if (!response.noDocumentsReason || response.noDocumentsReason.trim() === '') {
                errors[`criterionNoDocReason_${criterionIndex}`] = t('achievementsProof.documents.noDocumentsReason.error');
              }
            }
          });
        }
        break;

      default:
        break;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Track step progression
      const stepNameMapping = {
        'Category Selection': 'category_selection',
        'Phone Verification': 'phone_verification',
        'Personal Details': 'personal_details',
        'Farming Profile': 'farming_profile',
        'Achievements & Proof': 'criteria_responses',
        'Review & Submit': 'review_submit'
      };
      const currentStepName = getStepName(activeStep);
      logCustomEvent('form_step_completed', {
        step: activeStep,
        stepName: stepNameMapping[currentStepName] || currentStepName.toLowerCase().replace(/\s+/g, '_'),
        nextStep: nextStep,
        role: formData.role
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    // Validate all steps before submission
    const allStepsValid = [0, 1, 2, 3, 4].every(step => validateStep(step));
    if (!allStepsValid) {
      setError('Please complete all required fields in all sections');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

      // Step 1: Submit application data (without files)
      const applicationData = {
        role: formData.role,
        regionLevel: formData.regionLevel,
        category: formData.category,
        fullName: formData.fullName,
        aadharNumber: formData.aadharNumber || '',
        phoneNumber: formData.phoneNumber,
        state: formData.state,
        district: formData.district,
        taluk: formData.taluk,
        hobli: formData.hobli,
        village: formData.village,
        yearsOfExperience: formData.yearsOfExperience,
        domain: formData.domain,
        landUnderCultivation: formData.landUnderCultivation,
        cropDetails: formData.cropDetails,
        criteriaResponses: formData.criteriaResponses || []
      };

      console.log('Submitting application to backend...', applicationData);

      // Create submission
      const response = await fetch(`${apiUrl}/submissions/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicationData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit application');
      }

      const result = await response.json();
      console.log('Application submitted successfully:', result);

      const submissionId = result.submission.id;

      // Step 2: Upload documents from all criteria responses
      if (formData.criteriaResponses && formData.criteriaResponses.length > 0) {
        let totalDocuments = 0;
        let uploadedCount = 0;

        // Count total documents
        formData.criteriaResponses.forEach(response => {
          if (response.documents && response.documents.length > 0) {
            totalDocuments += response.documents.length;
          }
        });

        if (totalDocuments > 0) {
          console.log(`Uploading ${totalDocuments} documents...`);

          // Upload documents for each criterion
          for (let i = 0; i < formData.criteriaResponses.length; i++) {
            const criterionResponse = formData.criteriaResponses[i];

            if (criterionResponse.documents && criterionResponse.documents.length > 0) {
              for (let j = 0; j < criterionResponse.documents.length; j++) {
                const doc = criterionResponse.documents[j];
                const fileFormData = new FormData();
                fileFormData.append('files', doc.file);
                fileFormData.append('criterionName', criterionResponse.criterionName);

                uploadedCount++;
                console.log(`Uploading document ${uploadedCount}/${totalDocuments}: ${doc.file.name} for criterion: ${criterionResponse.criterionName}`);

                const uploadResponse = await fetch(`${apiUrl}/uploads/${submissionId}`, {
                  method: 'POST',
                  body: fileFormData,
                });

                if (!uploadResponse.ok) {
                  const uploadError = await uploadResponse.json();
                  console.error(`Failed to upload document ${uploadedCount}:`, uploadError);
                  // Continue with other uploads even if one fails
                } else {
                  const uploadResult = await uploadResponse.json();
                  console.log(`Document ${uploadedCount} uploaded:`, uploadResult);
                }
              }
            }
          }

          console.log('All documents uploaded successfully');
        }
      }

      setSubmitSuccess(true);
      setActiveStep(5);

      // Track successful form submission
      logCustomEvent('application_submitted', {
        role: formData.role,
        category: formData.category,
        regionLevel: formData.regionLevel,
        district: formData.district
      });
    } catch (err) {
      setError(err.message || 'Failed to submit application. Please try again.');
      console.error('Submission error:', err);

      // Track form submission failure
      logCustomEvent('application_submission_failed', {
        role: formData.role,
        category: formData.category,
        error: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    const stepName = getStepName(step);

    switch (stepName) {
      case t('stepper.categorySelection'):
        const regionalLevelOptions = {
          farmer: [
            { value: 'district', label: t('categorySelection.regionLevel.district') },
            { value: 'zonal', label: t('categorySelection.regionLevel.zonal') }
          ],
          fpo: [
            { value: 'zonal', label: t('categorySelection.regionLevel.zonal') }
          ],
          innovation: [
            { value: 'state', label: t('categorySelection.regionLevel.state') }
          ]
        };

        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <MapPin size={24} style={{ marginRight: 8, color: '#D4A747' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t('categorySelection.sectionTitle')}
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 2, md: 3 }}>
              {/* Role Selection */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  required
                  select
                  label={t('categorySelection.role.label')}
                  value={formData.role}
                  onChange={(e) => {
                    handleInputChange('role', e.target.value);
                    // Reset category and regional level when role changes
                    handleInputChange('category', '');
                    handleInputChange('regionLevel', '');
                  }}
                  error={!!formErrors.role}
                  helperText={formErrors.role}
                  size="small"
                  sx={modernFieldStyle}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Regional Level Selection - only show when role is selected */}
              {formData.role && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    required
                    select
                    label={t('categorySelection.regionLevel.label')}
                    value={formData.regionLevel}
                    onChange={(e) => {
                      handleInputChange('regionLevel', e.target.value);
                      // Reset category when regional level changes
                      handleInputChange('category', '');
                    }}
                    error={!!formErrors.regionLevel}
                    helperText={formErrors.regionLevel}
                    size="small"
                    sx={modernFieldStyle}
                  >
                    {(regionalLevelOptions[formData.role] || []).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {/* Award Categories as Cards - only show when role and regional level are selected */}
              {formData.role && formData.regionLevel && (
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2, color: '#1A1A1A', fontWeight: 600 }}>
                    {t('categorySelection.category.label')}
                  </Typography>

                  {loadingCategories ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress size={40} sx={{ color: '#D4A747' }} />
                      <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
                        {t('categorySelection.category.loading')}
                      </Typography>
                    </Box>
                  ) : filteredCategories.length === 0 ? (
                    <Alert severity="warning">
                      {t('categorySelection.category.noCategories')}
                    </Alert>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ mb: 2, color: '#666', fontSize: '0.875rem' }}>
                        {filteredCategories.length} {filteredCategories.length === 1 ? t('categorySelection.category.category') : t('categorySelection.category.categories')} {t('categorySelection.category.categoriesAvailable')}
                      </Typography>
                      <Grid container spacing={1.5}>
                        {filteredCategories.map((cat) => (
                          <Grid size={{ xs: 12, sm: 6 }} key={cat._id}>
                            <Paper
                              onClick={() => handleInputChange('category', cat._id)}
                              sx={{
                                p: 1.75,
                                cursor: 'pointer',
                                border: formData.category === cat._id
                                  ? '2px solid #D4A747'
                                  : '1px solid rgba(212, 167, 71, 0.15)',
                                borderRadius: '8px',
                                background: formData.category === cat._id
                                  ? 'linear-gradient(135deg, rgba(212, 167, 71, 0.08) 0%, rgba(255, 255, 255, 1) 100%)'
                                  : 'rgba(255, 255, 255, 1)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  borderColor: '#D4A747',
                                  boxShadow: '0 3px 10px rgba(212, 167, 71, 0.12)',
                                  transform: 'translateY(-1px)',
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, gap: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#B8901C', fontSize: '0.9rem', lineHeight: 1.3 }}>
                                  {cat.awardName}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                  <Chip
                                    label={cat.awardType}
                                    size="small"
                                    sx={{
                                      bgcolor: 'rgba(212, 167, 71, 0.1)',
                                      color: '#B8901C',
                                      fontWeight: 600,
                                      fontSize: '0.65rem',
                                      height: 20
                                    }}
                                  />
                                  {formData.category === cat._id && (
                                    <CheckCircle size={18} style={{ color: '#D4A747', flexShrink: 0 }} />
                                  )}
                                </Box>
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#666',
                                  lineHeight: 1.4,
                                  fontSize: '0.8rem',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}
                              >
                                {cat.description}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                  {formErrors.category && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {formErrors.category}
                    </Alert>
                  )}
                </Grid>
              )}
            </Grid>
          </motion.div>
        );

      case t('stepper.phoneVerification'):
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Phone size={24} style={{ marginRight: 8, color: '#D4A747' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t('phoneVerification.title')}
              </Typography>
            </Box>

            {!otpVerified ? (
              <Grid container spacing={{ xs: 2, md: 3 }}>
                {/* Display Eligibility Criteria */}
                {formData.category && categories.length > 0 && (() => {
                  const selectedCategory = categories.find(c => c._id === formData.category);
                  return selectedCategory && selectedCategory.qualificationCriteria && selectedCategory.qualificationCriteria.length > 0 ? (
                    <>
                      <Grid size={{ xs: 12 }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: '12px',
                            border: '2px solid rgba(212, 167, 71, 0.2)',
                            background: 'linear-gradient(135deg, rgba(212, 167, 71, 0.05) 0%, rgba(255, 255, 255, 1) 100%)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Award size={24} style={{ color: '#D4A747' }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#B8901C' }}>
                              {t('categorySelection.instructions.eligibilityCriteriaHeading')} {selectedCategory.awardName}
                            </Typography>
                          </Box>

                          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                            {t('categorySelection.instructions.title')}
                          </Typography>

                          <Box component="ul" sx={{ pl: 2, m: 0 }}>
                            {selectedCategory.qualificationCriteria.map((criterion, index) => (
                              <Box
                                component="li"
                                key={index}
                                sx={{
                                  mb: 1.5,
                                  color: '#1A1A1A',
                                  fontSize: '0.95rem',
                                  lineHeight: 1.6,
                                  '&::marker': {
                                    color: '#D4A747',
                                    fontWeight: 'bold'
                                  }
                                }}
                              >
                                {criterion}
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Alert severity="info">
                          <strong>{t('categorySelection.instructions.importantTitle')}</strong> {t('categorySelection.instructions.importantMessage')}
                          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                            <li>{t('categorySelection.instructions.documentsList.item1')}</li>
                            <li>{t('categorySelection.instructions.documentsList.item2')}</li>
                            <li>{t('categorySelection.instructions.documentsList.item3')}</li>
                          </Box>
                        </Alert>
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={hasReadInstructions}
                              onChange={(e) => {
                                setHasReadInstructions(e.target.checked);
                                if (e.target.checked && formErrors.hasReadInstructions) {
                                  setFormErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors.hasReadInstructions;
                                    return newErrors;
                                  });
                                }
                              }}
                              sx={{
                                color: formErrors.hasReadInstructions ? '#f44336' : '#D4A747',
                                '&.Mui-checked': {
                                  color: '#D4A747',
                                },
                              }}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                              {t('categorySelection.instructions.checkboxLabel')}
                            </Typography>
                          }
                        />
                        {formErrors.hasReadInstructions && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            {formErrors.hasReadInstructions}
                          </Alert>
                        )}
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 2 }} />
                      </Grid>
                    </>
                  ) : null;
                })()}

                <Grid size={{ xs: 12 }}>
                  <Alert severity="info">
                    {t('phoneVerification.subtitle')}
                  </Alert>
                </Grid>

                {/* Phone Number Input */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    required
                    label={t('phoneVerification.phoneNumber.label')}
                    value={phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setPhoneNumber(value);
                      if (formErrors.phoneNumber) {
                        setFormErrors(prev => ({ ...prev, phoneNumber: '' }));
                      }
                    }}
                    error={!!formErrors.phoneNumber}
                    helperText={formErrors.phoneNumber}
                    placeholder={t('phoneVerification.phoneNumber.placeholder')}
                    disabled={otpSent}
                    size="small"
                    sx={modernFieldStyle}
                    InputProps={{
                      startAdornment: (
                        <Typography sx={{ mr: 1, color: '#666' }}>+91</Typography>
                      ),
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  {!otpSent ? (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSendOtp}
                      disabled={sendingOtp || !phoneNumber || !hasReadInstructions}
                      sx={{
                        background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                        color: 'white',
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(212, 167, 71, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 6px 16px rgba(212, 167, 71, 0.4)'
                        },
                        '&:disabled': {
                          background: 'rgba(212, 167, 71, 0.3)',
                        }
                      }}
                    >
                      {sendingOtp ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                          {t('phoneVerification.buttons.sending')}
                        </>
                      ) : (
                        t('phoneVerification.buttons.verifyPhone')
                      )}
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => {
                        setOtpSent(false);
                        setOtp('');
                        setFormErrors({});
                      }}
                      sx={{
                        borderColor: '#D4A747',
                        color: '#D4A747',
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        '&:hover': {
                          borderColor: '#B8901C',
                          backgroundColor: 'rgba(212, 167, 71, 0.05)',
                        }
                      }}
                    >
                      {t('phoneVerification.buttons.changeNumber')}
                    </Button>
                  )}
                </Grid>

                {/* Warning Alert for unchecked checkbox */}
                {!hasReadInstructions && (
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      <strong>{t('categorySelection.instructions.checkboxError')}</strong><br />
                      {t('categorySelection.instructions.checkboxWarningMessage')}
                    </Alert>
                  </Grid>
                )}

                {/* OTP Input */}
                {otpSent && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Alert severity="success">
                        {t('phoneVerification.messages.otpSent')}
                      </Alert>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        required
                        label={t('phoneVerification.otp.label')}
                        value={otp}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setOtp(value);
                          if (formErrors.otp) {
                            setFormErrors(prev => ({ ...prev, otp: '' }));
                          }
                        }}
                        error={!!formErrors.otp}
                        helperText={formErrors.otp}
                        placeholder={t('phoneVerification.otp.placeholder')}
                        size="small"
                        sx={modernFieldStyle}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleVerifyOtp}
                        disabled={verifyingOtp || !otp}
                        sx={{
                          background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                          color: 'white',
                          py: 1.5,
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(212, 167, 71, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 16px rgba(212, 167, 71, 0.4)'
                          },
                          '&:disabled': {
                            background: 'rgba(212, 167, 71, 0.3)',
                          }
                        }}
                      >
                        {verifyingOtp ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                            {t('phoneVerification.buttons.verifying')}
                          </>
                        ) : (
                          t('phoneVerification.buttons.verifyOtp')
                        )}
                      </Button>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                        <Button
                          size="small"
                          onClick={handleSendOtp}
                          disabled={sendingOtp}
                          sx={{
                            color: '#D4A747',
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            '&:hover': {
                              backgroundColor: 'rgba(212, 167, 71, 0.05)',
                            }
                          }}
                        >
                          {t('phoneVerification.buttons.resendOtp')}
                        </Button>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            ) : userId === null ? (
              // User not found - show app download
              <Grid container spacing={{ xs: 2, md: 3 }}>
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">
                    <strong>{t('phoneVerification.messages.accountNotFound')}</strong><br />
                    {t('phoneVerification.appDownload.message')}
                  </Alert>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      borderRadius: '12px',
                      border: '2px solid rgba(212, 167, 71, 0.2)',
                      background: 'linear-gradient(135deg, rgba(212, 167, 71, 0.05) 0%, rgba(255, 255, 255, 1) 100%)',
                      maxWidth: '600px',
                      mx: 'auto',
                    }}
                  >
                    <Smartphone size={32} style={{ color: '#D4A747', marginBottom: '12px' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1 }}>
                      {t('phoneVerification.appDownload.title')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 3 }}>
                      {t('phoneVerification.appDownload.message')}
                    </Typography>

                    {/* QR Code */}
                    <Box
                      sx={{
                        width: '180px',
                        height: '180px',
                        mx: 'auto',
                        mb: 2,
                        borderRadius: '8px',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={bhoomeeQR}
                        alt={t('phoneVerification.appDownload.qrCodeAlt')}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    </Box>

                    <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 2 }}>
                      {t('phoneVerification.appDownload.scanToDownload')}
                    </Typography>

                    {/* App Store Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap', mt: -5 }}>
                      <a
                        href="https://play.google.com/store/apps/details?id=com.bhoomee"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'block' }}
                      >
                        <img
                          src={googlePlayImg}
                          alt={t('phoneVerification.appDownload.googlePlayAlt')}
                          style={{ height: '150px', cursor: 'pointer' }}
                        />
                      </a>
                      <a
                        href="https://apps.apple.com/app/bhoomee"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'block' }}
                      >
                        <img
                          src={appStoreImg}
                          alt={t('phoneVerification.appDownload.appStoreAlt')}
                          style={{ height: '150px', cursor: 'pointer' }}
                        />
                      </a>
                    </Box>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setOtpVerified(false);
                        setUserId(undefined);
                        setPhoneNumber('');
                        setOtp('');
                        setOtpSent(false);
                        setFormErrors({});
                        setError('');
                      }}
                      sx={{
                        borderColor: '#D4A747',
                        color: '#D4A747',
                        py: 1.5,
                        px: 4,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        '&:hover': {
                          borderColor: '#B8901C',
                          backgroundColor: 'rgba(212, 167, 71, 0.05)',
                        }
                      }}
                    >
                      {t('phoneVerification.messages.tryDifferentNumber')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              // User verified successfully
              <Grid container spacing={{ xs: 2, md: 3 }}>
                <Grid size={{ xs: 12 }}>
                  <Alert severity="success">
                    <strong>Phone Verified Successfully!</strong>
                    <br />
                    You can now proceed to the next step.
                  </Alert>
                </Grid>
              </Grid>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error}
              </Alert>
            )}
          </motion.div>
        );

      case 'Personal Details':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <User size={24} style={{ marginRight: 8, color: '#D4A747' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t('personalDetails.title')}
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  required
                  label={t('personalDetails.fields.fullName')}
                  value={formData.fullName}
                  disabled={formData.role !== 'innovation'}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  error={!!formErrors.fullName}
                  helperText={formErrors.fullName}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('personalDetails.fields.aadharNumber')}
                  value={formData.aadharNumber}
                  onChange={(e) => handleInputChange('aadharNumber', e.target.value)}
                  error={!!formErrors.aadharNumber}
                  helperText={formErrors.aadharNumber}
                  placeholder={t('personalDetails.fields.aadharPlaceholder')}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('personalDetails.fields.phoneNumber')}
                  value={formData.phoneNumber}
                  disabled
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  error={!!formErrors.phoneNumber}
                  helperText={formErrors.phoneNumber}
                  placeholder={t('personalDetails.fields.phonePlaceholder')}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, color: '#1A1A1A', fontWeight: 600 }}>
                  {t('personalDetails.locationTitle')}
                </Typography>
              </Grid>

              {/* State */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('personalDetails.fields.state')}
                  value={formData.state}
                  disabled={formData.role !== 'innovation'}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  helperText={formErrors.state}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              {/* District */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('personalDetails.fields.district')}
                  value={formData.district}
                  disabled={formData.role !== 'innovation'}
                  onChange={(e) => handleInputChange('district', e.target.value)}
                  error={!!formErrors.district}
                  helperText={formErrors.district || (formData.role === 'innovation' ? t('personalDetails.helperTexts.district') : t('personalDetails.autoFilled'))}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              {/* Taluk */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('personalDetails.fields.taluk')}
                  value={formData.taluk}
                  disabled={formData.role !== 'innovation'}
                  onChange={(e) => handleInputChange('taluk', e.target.value)}
                  error={!!formErrors.taluk}
                  helperText={formErrors.taluk || (formData.role === 'innovation' ? t('personalDetails.helperTexts.taluk') : t('personalDetails.autoFilled'))}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              {/* Hobli */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('personalDetails.fields.hobli')}
                  value={formData.hobli}
                  disabled={formData.role !== 'innovation'}
                  onChange={(e) => handleInputChange('hobli', e.target.value)}
                  error={!!formErrors.hobli}
                  helperText={formErrors.hobli || (formData.role === 'innovation' ? t('personalDetails.helperTexts.hobli') : t('personalDetails.autoFilled'))}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              {/* Village */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('personalDetails.fields.village')}
                  value={formData.village}
                  disabled={formData.role !== 'innovation'}
                  onChange={(e) => handleInputChange('village', e.target.value)}
                  error={!!formErrors.village}
                  helperText={formErrors.village || (formData.role === 'innovation' ? t('personalDetails.helperTexts.village') : t('personalDetails.autoFilled'))}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  {t('personalDetails.aadharWarning')}
                </Alert>
              </Grid>
            </Grid>
          </motion.div>
        );

      case 'Farming Profile':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Sprout size={24} style={{ marginRight: 8, color: '#D4A747' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t('farmingProfile.title')}
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  required
                  label={t('farmingProfile.fields.yearsOfExperience')}
                  type="number"
                  value={formData.yearsOfExperience}
                  onChange={(e) => handleInputChange('yearsOfExperience', e.target.value)}
                  error={!!formErrors.yearsOfExperience}
                  helperText={formErrors.yearsOfExperience}
                  inputProps={{ min: 0 }}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  required
                  select
                  label={t('farmingProfile.fields.farmingDomain')}
                  value={formData.domain}
                  onChange={(e) => handleInputChange('domain', e.target.value)}
                  error={!!formErrors.domain}
                  helperText={formErrors.domain}
                  size="small"
                  sx={modernFieldStyle}
                >
                  {farmingDomains.map((domain) => (
                    <MenuItem key={domain.value} value={domain.value}>
                      {domain.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  required
                  label={t('farmingProfile.fields.landUnderCultivation')}
                  type="number"
                  value={formData.landUnderCultivation}
                  onChange={(e) => handleInputChange('landUnderCultivation', e.target.value)}
                  error={!!formErrors.landUnderCultivation}
                  helperText={formErrors.landUnderCultivation}
                  inputProps={{ min: 0, step: 0.1 }}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  required
                  label={t('farmingProfile.fields.cropDetails')}
                  multiline
                  rows={4}
                  value={formData.cropDetails}
                  onChange={(e) => handleInputChange('cropDetails', e.target.value)}
                  error={!!formErrors.cropDetails}
                  helperText={formErrors.cropDetails || t('farmingProfile.cropDetailsHelper')}
                  placeholder={t('farmingProfile.cropDetailsPlaceholder')}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  {t('farmingProfile.infoMessage')}
                </Alert>
              </Grid>
            </Grid>
          </motion.div>
        );

        case t('stepper.achievementsProof'):
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Award size={24} style={{ marginRight: 8, color: '#D4A747' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t('achievementsProof.title')}
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 1.5, md: 2 }}>
              {/* Loop through each criterion */}
              {formData.criteriaResponses && formData.criteriaResponses.length > 0 ? (
                formData.criteriaResponses.map((response, criterionIndex) => (
                  <React.Fragment key={criterionIndex}>
                    {/* Criterion Header */}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#B8901C', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Award size={18} />
                        {t('achievementsProof.criterionLabel')} {criterionIndex + 1}: {response.criterionName}
                      </Typography>
                    </Grid>

                    {/* Achievement Description for this criterion */}
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        required
                        multiline
                        rows={3}
                        label={t('achievementsProof.fields.achievementDescription')}
                        value={response.achievementDescription}
                        onChange={(e) => handleCriterionAchievementChange(criterionIndex, e.target.value)}
                        error={!!formErrors[`criterionAchievement_${criterionIndex}`]}
                        helperText={
                          formErrors[`criterionAchievement_${criterionIndex}`] ||
                          `${response.achievementDescription.length}/50 ${t('achievementsProof.achievementDescription.characterCount')}`
                        }
                        placeholder={t('achievementsProof.achievementDescription.placeholder')}
                        sx={modernFieldStyle}
                      />
                    </Grid>

                    {/* File Upload for this criterion */}
                    <Grid size={{ xs: 12 }}>
                      <Box
                        sx={{
                          border: '2px dashed rgba(212, 167, 71, 0.3)',
                          borderRadius: '8px',
                          p: 1.5,
                          textAlign: 'center',
                          backgroundColor: 'rgba(212, 167, 71, 0.02)',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#D4A747',
                            backgroundColor: 'rgba(212, 167, 71, 0.05)',
                          }
                        }}
                        onClick={() => document.getElementById(`file-upload-criterion-${criterionIndex}`).click()}
                      >
                        <input
                          id={`file-upload-criterion-${criterionIndex}`}
                          type="file"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleCriterionFileUpload(criterionIndex, e)}
                          style={{ display: 'none' }}
                        />
                        <Upload size={24} style={{ color: '#D4A747', marginBottom: '4px' }} />
                        <Typography variant="body2" sx={{ color: '#1A1A1A', fontWeight: 600 }}>
                          {t('achievementsProof.documents.uploadTitle')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                          {t('achievementsProof.documents.uploadHelperText')}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Display Uploaded Files for this criterion */}
                    {response.documents && response.documents.length > 0 && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="caption" sx={{ mb: 1, fontWeight: 600, color: '#666', display: 'block' }}>
                          {response.documents.length} {response.documents.length > 1 ? t('achievementsProof.documents.documents') : t('achievementsProof.documents.document')} {t('achievementsProof.documents.documentsUploaded')}
                        </Typography>
                        <Grid container spacing={1}>
                          {response.documents.map((doc) => (
                            <Grid size={{ xs: 12 }} key={doc.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid rgba(212, 167, 71, 0.2)', borderRadius: '6px', backgroundColor: 'rgba(212, 167, 71, 0.02)' }}>
                                <FileText size={16} style={{ color: '#D4A747', flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem', color: '#1A1A1A' }}>
                                  {doc.file.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#999', fontSize: '0.75rem' }}>
                                  {(doc.file.size / 1024).toFixed(1)} {t('achievementsProof.documents.fileSizeUnit')}
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={() => handleCriterionRemoveDocument(criterionIndex, doc.id)}
                                  sx={{
                                    minWidth: 'auto',
                                    p: 0.5,
                                    color: '#f44336',
                                    '&:hover': {
                                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                    }
                                  }}
                                >
                                  <X size={16} />
                                </Button>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    )}

                    {/* No Documents Reason for this criterion */}
                    {(!response.documents || response.documents.length === 0) && (
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          required
                          multiline
                          rows={2}
                          label={t('achievementsProof.documents.noDocumentsReason.label')}
                          value={response.noDocumentsReason}
                          onChange={(e) => handleCriterionNoDocReasonChange(criterionIndex, e.target.value)}
                          error={!!formErrors[`criterionNoDocReason_${criterionIndex}`]}
                          helperText={formErrors[`criterionNoDocReason_${criterionIndex}`] || t('achievementsProof.documents.noDocumentsReason.helperText')}
                          placeholder={t('achievementsProof.documents.noDocumentsReason.placeholder')}
                          size="small"
                          sx={modernFieldStyle}
                        />
                      </Grid>
                    )}

                    {/* Divider between criteria */}
                    {criterionIndex < formData.criteriaResponses.length - 1 && (
                      <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 2 }} />
                      </Grid>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info">
                    {t('achievementsProof.noCategoryMessage')}
                  </Alert>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  <strong>{t('achievementsProof.noteTitle')}</strong> {t('achievementsProof.noteMessage')}
                </Alert>
              </Grid>
            </Grid>
          </motion.div>
        );

      case 'Review & Submit':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                textAlign: 'center',
                mb: 4,
                pb: 3,
                borderBottom: '2px solid rgba(212, 167, 71, 0.1)'
              }}
            >
              <CheckCircle size={40} style={{ color: '#D4A747', marginBottom: '12px' }} />
              <Typography
                variant="h5"
                sx={{
                  color: '#1A1A1A',
                  fontWeight: 700,
                  mb: 1
                }}
              >
                {t('reviewSubmit.title')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  fontSize: '0.95rem'
                }}
              >
                {t('reviewSubmit.verificationMessage')}
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 2, md: 2.5 }}>
              {/* Region & Category */}
              <Grid size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: '10px',
                    border: '1px solid rgba(212, 167, 71, 0.15)',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(212, 167, 71, 0.02) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(212, 167, 71, 0.3)',
                      boxShadow: '0 4px 12px rgba(212, 167, 71, 0.1)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#B8901C',
                        fontSize: '1rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <MapPin size={18} />
                      Role & Category
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setActiveStep(steps.indexOf('Category Selection'))}
                      startIcon={<Edit size={14} />}
                      sx={{
                        color: '#D4A747',
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '6px',
                        border: '1px solid rgba(212, 167, 71, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(212, 167, 71, 0.08)',
                          borderColor: 'rgba(212, 167, 71, 0.4)',
                        }
                      }}
                    >
                      Edit
                    </Button>
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Role
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                        {roles.find(r => r.value === formData.role)?.label}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Award Category
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                        {categories.find(c => c._id === formData.category)?.awardName || formData.category}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Personal Details */}
              <Grid size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: '10px',
                    border: '1px solid rgba(212, 167, 71, 0.15)',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(212, 167, 71, 0.02) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(212, 167, 71, 0.3)',
                      boxShadow: '0 4px 12px rgba(212, 167, 71, 0.1)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#B8901C',
                        fontSize: '1rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <User size={18} />
                      {t('personalDetails.sectionTitle')}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setActiveStep(steps.indexOf('Personal Details'))}
                      startIcon={<Edit size={14} />}
                      sx={{
                        color: '#D4A747',
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '6px',
                        border: '1px solid rgba(212, 167, 71, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(212, 167, 71, 0.08)',
                          borderColor: 'rgba(212, 167, 71, 0.4)',
                        }
                      }}
                    >
                      Edit
                    </Button>
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Full Name
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                        {formData.fullName}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Aadhar Number
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                        {formData.aadharNumber}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Phone
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                        {formData.phoneNumber}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#B8901C', mb: 1 }}>
                        Location Details
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        State
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                        {formData.state}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        District
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                        {formData.district}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Taluk
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                        {formData.taluk}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Hobli
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                        {formData.hobli}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Village
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                        {formData.village}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Farming Profile - Only show for farmers */}
              {formData.role === 'farmer' && (
                <Grid size={{ xs: 12 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: '10px',
                      border: '1px solid rgba(212, 167, 71, 0.15)',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(212, 167, 71, 0.02) 100%)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'rgba(212, 167, 71, 0.3)',
                        boxShadow: '0 4px 12px rgba(212, 167, 71, 0.1)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#B8901C',
                          fontSize: '1rem',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <Sprout size={18} />
                        {t('farmingProfile.sectionTitle')}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setActiveStep(steps.indexOf(t('stepper.farmingProfile')))}
                        startIcon={<Edit size={14} />}
                        sx={{
                          color: '#D4A747',
                          fontSize: '0.8rem',
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '6px',
                          border: '1px solid rgba(212, 167, 71, 0.2)',
                          '&:hover': {
                            backgroundColor: 'rgba(212, 167, 71, 0.08)',
                            borderColor: 'rgba(212, 167, 71, 0.4)',
                          }
                        }}
                      >
                        Edit
                      </Button>
                    </Box>
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Years of Experience
                        </Typography>
                        <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                          {formData.yearsOfExperience} years
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Domain
                        </Typography>
                        <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                          {getDomainLabel(formData.domain)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Land Under Cultivation
                        </Typography>
                        <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                          {formData.landUnderCultivation} acres
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Crop Details
                        </Typography>
                        <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                          {formData.cropDetails}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}

              {/* Achievements & Proof */}
              <Grid size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: '10px',
                    border: '1px solid rgba(212, 167, 71, 0.15)',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(212, 167, 71, 0.02) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(212, 167, 71, 0.3)',
                      boxShadow: '0 4px 12px rgba(212, 167, 71, 0.1)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#B8901C',
                        fontSize: '1rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Award size={18} />
                      {t('achievementsProof.title')}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setActiveStep(steps.indexOf('Achievements & Proof'))}
                      startIcon={<Edit size={14} />}
                      sx={{
                        color: '#D4A747',
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '6px',
                        border: '1px solid rgba(212, 167, 71, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(212, 167, 71, 0.08)',
                          borderColor: 'rgba(212, 167, 71, 0.4)',
                        }
                      }}
                    >
                      Edit
                    </Button>
                  </Box>
                  <Grid container spacing={1.5}>
                    {formData.criteriaResponses && formData.criteriaResponses.length > 0 ? (
                      formData.criteriaResponses.map((response, index) => (
                        <React.Fragment key={index}>
                          <Grid size={{ xs: 12 }}>
                            <Typography sx={{ fontSize: '0.75rem', color: '#B8901C', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>
                              Criteria {index + 1}: {response.criterionName}
                            </Typography>
                            <Typography sx={{ fontSize: '0.9rem', color: '#1A1A1A', fontWeight: 500, whiteSpace: 'pre-wrap', mb: 1 }}>
                              {response.achievementDescription}
                            </Typography>

                            {response.documents && response.documents.length > 0 ? (
                              <Box sx={{ mt: 1 }}>
                                <Typography sx={{ fontSize: '0.7rem', color: '#999', mb: 0.5 }}>
                                  {response.documents.length} document{response.documents.length > 1 ? 's' : ''} uploaded
                                </Typography>
                                {response.documents.map((doc) => (
                                  <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <FileText size={14} style={{ color: '#D4A747' }} />
                                    <Typography sx={{ fontSize: '0.85rem', color: '#666' }}>
                                      {doc.file.name}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography sx={{ fontSize: '0.8rem', color: '#999', fontStyle: 'italic', mt: 0.5 }}>
                                No documents: {response.noDocumentsReason}
                              </Typography>
                            )}
                          </Grid>
                          {index < formData.criteriaResponses.length - 1 && (
                            <Grid size={{ xs: 12 }}>
                              <Divider sx={{ my: 1 }} />
                            </Grid>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <Grid size={{ xs: 12 }}>
                        <Typography sx={{ fontSize: '0.9rem', color: '#999', fontStyle: 'italic' }}>
                          No criteria responses provided
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                  color: 'white',
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: '12px',
                  boxShadow: '0 4px 16px rgba(212, 167, 71, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(212, 167, 71, 0.4)'
                  },
                  '&:disabled': {
                    background: 'rgba(212, 167, 71, 0.3)',
                  }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    {t('reviewSubmit.buttons.submitting')}
                  </>
                ) : (
                  t('reviewSubmit.buttons.submit')
                )}
              </Button>
            </Box>
          </motion.div>
        );

      default:
        return null;
    }
  };

  if (submitSuccess) {
    return (
      <Box sx={{ backgroundColor: '#FFFFFF' }}>
        <Box
          sx={{
            position: 'relative',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            overflow: 'hidden',
            backgroundImage: `url("https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=1920&h=1080&fit=crop")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle, rgba(212, 165, 32, 0.5) 0%, rgba(0, 0, 0, 0.9) 100%, rgba(0, 0, 0, 0.9) 100%);',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />

          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <CheckCircle
                size={80}
                style={{ color: theme.palette.primary.main, marginBottom: '24px' }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 'bold',
                  mb: 3,
                  lineHeight: 1.2,
                  background: 'linear-gradient(135deg, #1A1A1A 27%, #D4A747 50%, #1A1A1A 73%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('messages.successTitle')}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  mb: 4,
                  opacity: 0.9,
                  maxWidth: 700,
                  mx: 'auto',
                  color: theme.palette.secondary.main,
                }}
              >
                {t('messages.successMessage')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 5,
                  opacity: 0.8,
                  color: '#CCCCCC',
                }}
              >
                {t('messages.confirmationEmail')}
              </Typography>
              <Chip
                label={`${t('messages.applicationId')}: APP${Date.now().toString().slice(-8)}`}
                sx={{
                  bgcolor: '#D4A747',
                  color: '#1A1A1A',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  px: 2,
                  py: 3,
                  mb: 4
                }}
              />
              <Box sx={{ mt: 4 }}>
                <Button
                  variant="outlined"
                  href="/nominations"
                  sx={{
                    borderColor: '#D4A747',
                    color: '#D4A747',
                    mr: 2,
                    py: 1.5,
                    px: 4,
                    borderRadius: '12px',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#B8901C',
                      bgcolor: 'rgba(212, 167, 71, 0.1)',
                    },
                  }}
                >
                  {t('messages.viewAllCategories')}
                </Button>
                <Button
                  variant="contained"
                  href="/"
                  size="large"
                  sx={{
                    background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    py: 1.5,
                    px: 5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    boxShadow: '0 8px 24px rgba(212, 167, 71, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(212, 167, 71, 0.4)'
                    }
                  }}
                >
                  {t('messages.backToHome')}
                </Button>
              </Box>
            </motion.div>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#FFFFFF' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          pt: { xs: "160px", lg: "120px" },
          pb: { xs: "60px", lg: "80px" },
          minHeight: { xs: '40vh', md: '50vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          overflow: 'visible',
          backgroundImage: `url("https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=1920&h=1080&fit=crop")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle, rgba(212, 165, 32, 0.5) 0%, rgba(0, 0, 0, 0.9) 100%, rgba(0, 0, 0, 0.9) 100%);',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'secondary.light',
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: '0.875rem',
              }}
            >
              {t('hero.overline')}
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 'bold',
                mb: 3,
                lineHeight: 1.1,
                background: 'linear-gradient(135deg, #1A1A1A 27%, #D4A747 50%, #1A1A1A 73%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {t('hero.title')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                opacity: 0.8,
                maxWidth: 600,
                color: theme.palette.secondary.main,
                mx: 'auto'
              }}
            >
              {t('hero.description')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Form Section */}
      <Box sx={{ py: { xs: 4, md: 8 }, bgcolor: 'white' }}>
        <Container
          maxWidth="lg"
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            maxWidth: { xs: '100%', sm: '100%', md: 'lg' }
          }}
        >
          {/* Stepper */}
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2, md: 4 }, mb: { xs: 2, md: 4 }, border: '1px solid rgba(212, 167, 71, 0.15)', overflow: 'hidden', position: 'relative' }}>
            {/* Left Arrow - Only visible on mobile when scrollable */}
            {showLeftArrow && (
              <IconButton
                onClick={() => scrollStepper('left')}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(212, 167, 71, 0.1)',
                  },
                  width: 32,
                  height: 32,
                }}
              >
                <ChevronLeft size={20} style={{ color: '#D4A747' }} />
              </IconButton>
            )}

            {/* Right Arrow - Only visible on mobile when scrollable */}
            {showRightArrow && (
              <IconButton
                onClick={() => scrollStepper('right')}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(212, 167, 71, 0.1)',
                  },
                  width: 32,
                  height: 32,
                }}
              >
                <ChevronRight size={20} style={{ color: '#D4A747' }} />
              </IconButton>
            )}

            <Box
              ref={stepperRef}
              sx={{
                overflowX: { xs: 'auto', md: 'visible' },
                overflowY: 'hidden',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                px: { xs: showLeftArrow || showRightArrow ? 5 : 0, md: 0 },
              }}
            >
              <Stepper activeStep={activeStep} connector={<CustomStepConnector />} sx={{ minWidth: 'max-content' }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel
                      sx={{
                        '& .MuiStepLabel-label': {
                          fontSize: { xs: '0.75rem', md: '0.875rem' },
                          fontWeight: 500,
                        },
                        '& .MuiStepLabel-label.Mui-active': {
                          color: '#D4A747',
                          fontWeight: 600,
                        },
                        '& .MuiStepLabel-label.Mui-completed': {
                          color: '#B8901C',
                          fontWeight: 600,
                        },
                        '& .MuiStepIcon-root': {
                          color: 'rgba(212, 167, 71, 0.2)',
                        },
                        '& .MuiStepIcon-root.Mui-active': {
                          color: '#D4A747',
                        },
                        '& .MuiStepIcon-root.Mui-completed': {
                          color: '#B8901C',
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Paper>

          {/* Form Content */}
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3, md: 5 }, border: '1px solid rgba(212, 167, 71, 0.15)', overflow: 'hidden' }}>
            <AnimatePresence mode="wait">
              {renderStepContent(activeStep)}
            </AnimatePresence>

            {/* Navigation Buttons */}
            {activeStep < 5 && (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 }, justifyContent: 'space-between', mt: { xs: 3, md: 4 }, pt: { xs: 2, md: 3 }, borderTop: '1px solid rgba(212, 167, 71, 0.1)' }}>
                <Button
                  disabled={activeStep === 0 || loading}
                  onClick={handleBack}
                  startIcon={<ArrowLeft size={18} />}
                  sx={{
                    color: '#666',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(212, 167, 71, 0.05)',
                    },
                    '&:disabled': {
                      color: '#ccc',
                    }
                  }}
                >
                  {t('navigation.back')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={loading}
                  endIcon={<ArrowRight size={18} />}
                  sx={{
                    background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                    color: 'white',
                    px: 4,
                    py: 1,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(212, 167, 71, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 16px rgba(212, 167, 71, 0.4)'
                    },
                    '&:disabled': {
                      background: 'rgba(212, 167, 71, 0.3)',
                    }
                  }}
                >
                  {t('navigation.next')}
                </Button>
              </Box>
            )}

            {activeStep === 5 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 4, pt: 3, borderTop: '1px solid rgba(212, 167, 71, 0.1)' }}>
                <Button
                  onClick={handleBack}
                  disabled={loading}
                  startIcon={<ArrowLeft size={18} />}
                  sx={{
                    color: '#666',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(212, 167, 71, 0.05)',
                    },
                    '&:disabled': {
                      color: '#ccc',
                    }
                  }}
                >
                  {t('navigation.back')}
                </Button>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default AwardsApplication;
